import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { Card } from 'primereact/card';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Tag } from 'primereact/tag';
import { Button } from 'primereact/button';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { documentService } from '../services/documentService';
import LoadingSpinner from '../components/LoadingSpinner';

const DashboardPage = () => {
  const { user, hasAnyRole } = useAuth();
  const navigate = useNavigate();

  const { data: documentsData, isLoading } = useQuery({
    queryKey: ['documents', { limit: 5, sortBy: 'createdAt', sortOrder: 'DESC' }],
    queryFn: () => documentService.getDocuments({ limit: 5, sortBy: 'createdAt', sortOrder: 'DESC' }),
  });

  const getStatusSeverity = (status) => {
    switch (status) {
      case 'PUBLISHED':
        return 'success';
      case 'DRAFT':
        return 'info';
      case 'REVOKED':
        return 'danger';
      case 'ARCHIVED':
        return 'warning';
      default:
        return 'info';
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  if (isLoading) {
    return <LoadingSpinner message="Loading dashboard..." />;
  }

  const recentDocuments = documentsData?.documents || [];

  return (
    <div className="grid">
      {/* Welcome Card */}
      <div className="col-12">
        <Card>
          <div className="flex align-items-center justify-content-between">
            <div>
              <h2 className="m-0 mb-2">Welcome back, {user?.firstName}!</h2>
              <p className="text-muted m-0">
                Manage and verify LHRC documents with ease.
              </p>
            </div>
            <div className="text-right">
              <p className="text-sm text-muted mb-1">Role</p>
              <Tag value={user?.role} severity="info" />
            </div>
          </div>
        </Card>
      </div>

      {/* Quick Actions */}
      <div className="col-12 md:col-6 lg:col-4">
        <Card>
          <h3 className="mt-0 mb-3">Quick Actions</h3>
          <div className="flex flex-column gap-2">
            <Button
              label="View All Documents"
              icon="pi pi-file"
              className="p-button-outlined"
              onClick={() => navigate('/documents')}
            />
            {hasAnyRole(['ADMIN', 'EDITOR']) && (
              <Button
                label="Upload Document"
                icon="pi pi-upload"
                className="p-button-outlined"
                onClick={() => navigate('/upload')}
              />
            )}
            {hasAnyRole(['ADMIN']) && (
              <Button
                label="Admin Panel"
                icon="pi pi-cog"
                className="p-button-outlined"
                onClick={() => navigate('/admin')}
              />
            )}
          </div>
        </Card>
      </div>

      {/* Statistics */}
      <div className="col-12 md:col-6 lg:col-4">
        <Card>
          <h3 className="mt-0 mb-3">Statistics</h3>
          <div className="grid">
            <div className="col-6">
              <div className="text-center">
                <div className="text-2xl font-bold text-primary">
                  {documentsData?.pagination?.total || 0}
                </div>
                <div className="text-sm text-muted">Total Documents</div>
              </div>
            </div>
            <div className="col-6">
              <div className="text-center">
                <div className="text-2xl font-bold text-green-500">
                  {recentDocuments.filter(doc => doc.status === 'PUBLISHED').length}
                </div>
                <div className="text-sm text-muted">Published</div>
              </div>
            </div>
          </div>
        </Card>
      </div>

      {/* Recent Documents */}
      <div className="col-12 lg:col-4">
        <Card>
          <div className="flex align-items-center justify-content-between mb-3">
            <h3 className="mt-0">Recent Documents</h3>
            <Button
              label="View All"
              icon="pi pi-external-link"
              className="p-button-text p-button-sm"
              onClick={() => navigate('/documents')}
            />
          </div>
          
          {recentDocuments.length === 0 ? (
            <p className="text-muted text-center">No documents found</p>
          ) : (
            <div className="flex flex-column gap-2">
              {recentDocuments.slice(0, 3).map((doc) => (
                <div key={doc.id} className="border-1 surface-border p-3 border-round">
                  <div className="flex align-items-center justify-content-between mb-2">
                    <h4 className="m-0 text-sm">{doc.title}</h4>
                    <Tag
                      value={doc.status}
                      severity={getStatusSeverity(doc.status)}
                    />
                  </div>
                  <div className="text-xs text-muted">
                    <div>Code: {doc.shortCode}</div>
                    <div>Size: {formatFileSize(doc.fileSize)}</div>
                    <div>Uploaded: {formatDate(doc.createdAt)}</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>

      {/* Recent Documents Table */}
      <div className="col-12">
        <Card>
          <div className="flex align-items-center justify-content-between mb-3">
            <h3 className="mt-0">Recent Documents</h3>
            <Button
              label="View All Documents"
              icon="pi pi-external-link"
              className="p-button-text"
              onClick={() => navigate('/documents')}
            />
          </div>
          
          <DataTable
            value={recentDocuments}
            paginator={false}
            emptyMessage="No documents found"
            className="p-datatable-sm"
          >
            <Column
              field="title"
              header="Title"
              sortable
              style={{ minWidth: '200px' }}
            />
            <Column
              field="shortCode"
              header="Code"
              sortable
              style={{ width: '100px' }}
            />
            <Column
              field="status"
              header="Status"
              body={(rowData) => (
                <Tag
                  value={rowData.status}
                  severity={getStatusSeverity(rowData.status)}
                />
              )}
              style={{ width: '120px' }}
            />
            <Column
              field="fileSize"
              header="Size"
              body={(rowData) => formatFileSize(rowData.fileSize)}
              sortable
              style={{ width: '100px' }}
            />
            <Column
              field="createdAt"
              header="Uploaded"
              body={(rowData) => formatDate(rowData.createdAt)}
              sortable
              style={{ width: '120px' }}
            />
            <Column
              header="Actions"
              body={(rowData) => (
                <Button
                  icon="pi pi-eye"
                  className="p-button-text p-button-sm"
                  tooltip="View Details"
                  onClick={() => navigate(`/documents/${rowData.id}`)}
                />
              )}
              style={{ width: '80px' }}
            />
          </DataTable>
        </Card>
      </div>
    </div>
  );
};

export default DashboardPage;